import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

import PetsRaterAnswers from '@/modules/pets/rater/models/APP/PetsRaterAnswers'
import PetsHiringAnswers from '@/modules/pets/hiring/models/APP/PetsHiringAnswers'
import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import PetsConfigurations from '@/modules/pets/common/models/APP/PetsConfigurations'
import PetsInsuranceProcessStepEnum from '@/modules/pets/hiring/models/APP/PetsInsuranceProcessStepEnum'
import {
  ButtonTypeEnum,
  ErrorDisplayTypeEnum,
  InsuranceProcessStatusEnum,
  InsuranceState,
  InsuranceStateName
} from '@/modules/core/models/enums'
import MiddlewareService from '@/modules/core/services/MiddlewareService'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { useMessageStore } from '@/modules/core/stores/messageStore'
import DXPService from '@/modules/core/services/DXPService'
import PetsCommonMapper from '@/modules/pets/common/mappers'
import PetsMapper from '@/modules/pets/hiring/mappers'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import CustomError from '@/modules/core/models/errors/CustomError'
import type PetsInsurance from '../../common/models/APP/PetsInsurance'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import { checkRecoveredQuotationBranch } from '@/modules/core/utils/BranchUtils'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'
import { GetPetBreedRequest } from '../models/MID/requests/GetPetBreedRequest'
import { GetPetBreedResponse } from '@/modules/pets/hiring/models/MID/responses/GetPetBreedResponse'
import PetsGetRecoveryResponse from '../models/MID/responses/PetsGetRecoveryResponse'
import router from '@/router'
import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
import type { PetBreed } from '../models/MID/Breed'
import { RetrieveCoveragesCompleteRequest } from '@/modules/common/common/models/MID/requests/RetrieveCoveragesCompleteRequest'
import { RetrieveCoveragesCompleteResponse } from '@/modules/common/common/models/MID/responses/RetrieveCoveragesCompleteResponse'
import { CURRENCY } from '@/modules/common/common/constants/CommonConstants'
import ValidateChip from '../models/MID/responses/ValidateChipResponse'
import { needsAccidentCoverageQuestion, needsCivilLiabilityQuestion } from '../utils'
import { priceFormat } from '@/utils/textUtils'
import Message from '@/modules/core/models/modalMessage/Message'

// singleton items
const coverages = ref<{ pendingRequest?: Promise<any>; value?: RetrieveCoveragesCompleteResponse }>(
  {}
)
const breeds = ref<Record<string, { pendingRequest?: Promise<any>; value?: PetBreed[] }>>({})
const globalConfig = ref(new PetsConfigurations())

export const usePetsStore = defineStore('pets', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()
  const messageStore = useMessageStore()

  const { t } = useI18n()

  const hiringAnswers = ref(new PetsHiringAnswers())

  const stepsBeforeSaveAndExit = computed(() => {
    const stepCode = hiringAnswers.value.processStatus?.stepCode ?? 0
    const steps = []

    steps.push({
      // insurance data
      label: t('copy_save-and-exit-modal_step-insurance'),
      check: true
    })

    steps.push({
      // document step
      label: t('copy_save-and-exit-modal_step-document'),
      check:
        stepCode >= PetsInsuranceProcessStepEnum.DOCUMENTS &&
        !!hiringAnswers.value.policyHolder.documentDetails.documentNumber
    })

    steps.push({
      // policy holder step
      label: t('copy_save-and-exit-modal_step-holder-TN'),
      check:
        stepCode >= PetsInsuranceProcessStepEnum.POLICY_HOLDER_SECOND &&
        !!hiringAnswers.value.policyHolder.userDetails.email &&
        (hiringAnswers.value.policyHolder.preContractualDocumentChecked ||
          stepCode > PetsInsuranceProcessStepEnum.POLICY_HOLDER_SECOND)
    })

    hiringAnswers.value.pets.forEach((pet, i) => {
      const extraStep =
        needsCivilLiabilityQuestion(pet.coverages) || needsAccidentCoverageQuestion(pet.coverages)
      // pet steps
      steps.push({
        label: t('copy_save_and_exit_modal_step-pets', { n: i + 1 }),
        check:
          stepCode >= PetsInsuranceProcessStepEnum.PETS &&
          ((!extraStep && !!pet.chipNumber) ||
            (extraStep &&
              (typeof pet.hasAccidentInsurance === 'boolean' ||
                typeof pet.hasRcInsurance === 'boolean')))
      })
    })

    const needsCivilLiability = hiringAnswers.value.needsCivilLiabilityStep()
    if (needsCivilLiability) {
      steps.push({
        label: t('copy_save_and_exit_modal_step-civil-liability'),
        check:
          stepCode >= PetsInsuranceProcessStepEnum.CIVIL_LIABILITY &&
          (!!hiringAnswers.value.civilLiabilityInsured.userDetails.email ||
            !!hiringAnswers.value.isLiabilityInsuredPolicyholder)
      })
    }

    return steps
  })

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function setGloballyAnswers(answers: Partial<PetsHiringAnswers>) {
    hiringAnswers.value.updateAttributes(answers as any)
  }

  function getHiringAnswers(): PetsHiringAnswers {
    return hiringAnswers.value as PetsHiringAnswers
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(products: ProductId[]): Promise<DXPProduct[]> {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = PetsCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): PetsInsurance[] {
    const products: PetsInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = PetsCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
        products.push(insurance)
      }
    })

    return products
  }

  async function loadConfiguration(reset = false) {
    try {
      if (globalConfig.value?.businessLineId && !reset) {
        hiringAnswers.value.businessLine = globalConfig.value.businessLineId
        return
      }

      const endpoint = ENDPOINTS.DXP_GET_BRANCH_PETS
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      globalConfig.value = new PetsConfigurations(data)
      hiringAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function getNewRaterPrice(
    answers: PetsRaterAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    isQuote: boolean = false,
    productCode: string | null | undefined = undefined
  ): Promise<PetsInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_PETS
      const request = PetsCommonMapper.GetPricingMapper.toRequest(
        answers,
        isQuote,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)

      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      const insurances = _loadCompleteSheetProducts(dxpData, data.listPricing)
      return (
        (productCode ? insurances.find((i) => i.productCode === productCode) : insurances[0]) ??
        null
      )
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: PetsInsurance
  ) {
    let valid = true

    try {
      loadingStore.startLoading('common.loading.processing')

      if (!quotationId) throw new Error('Quotation id is required')

      const endpoint = ENDPOINTS.SAVE_QUOTATION_PETS
      const request = PetsCommonMapper.SavePetsQuotationMapper.toRequest(
        quotationId,
        startDate,
        billingCycle,
        product,
        hiringAnswers.value.pets.map((pet, i) => ({ name: pet.petName, riskNumber: String(i + 1) }))
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    }

    return { valid }
  }

  async function fetchHiringFormAnswers(quotationId: string) {
    try {
      commonStore.showLogo = false
      const endpoint = `${ENDPOINTS.GET_RECOVERY_PETS}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }
      await commonStore.resolveMediatorFromRecovery(data.mediator?.mediator)
      await loadConfiguration(true)
      const branchConfig = getBranchConfiguration()
      const itsOk = await checkRecoveredQuotationBranch(
        data.hiring.businessLine,
        branchConfig.businessLineId,
        quotationId
      )
      if (!itsOk) return
      const { productCode, clientCode } = data.hiring
      const product = new ProductId({ productCode, clientCode })
      const dxpData = await _fetchDXPInsurances([product])
      if (!dxpData.length) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }

      const DXPProduct = dxpData[0]
      const MIDProduct = data.hiring
      const insurance = PetsCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
      hiringAnswers.value = PetsMapper.GetRecoveryMapper.fromResponse(data, insurance)
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
    } finally {
      commonStore.showLogo = true
    }
  }

  async function getEmittedPolicyStatus(quotationId: string) {
    try {
      const endpoint = `${ENDPOINTS.GET_RECOVERY_PETS}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpoint)
      const resp = new PetsGetRecoveryResponse(data)
      if (!error && resp.hiring.statusDescription === InsuranceStateName.REJECTED) {
        router.push({ name: 'hiring-pets', params: { id: quotationId } })
        return null
      }
      if (
        error ||
        (resp.hiring.statusDescription !== InsuranceStateName.ISSUED &&
          resp.hiring.statusDescription !== InsuranceStateName.PENDING)
      )
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setTitle('copy_error_emission_service_title')
          .setModalDescription('copy_error_emission_service_description')
          .setExtra({ hiringNumber: resp.hiring.quotationNumber })
          .setButtons([
            new ModalButton({
              text: 'copy_button_emission_contact',
              callback: () => useSupportModalStore().setOpenModal(true)
            }),
            new ModalButton({
              text: 'copy_button_emission_exit',
              navTo: 'exit',
              type: ButtonTypeEnum.OUTLINE
            })
          ])

      return Object.values(InsuranceStateName).indexOf(resp.hiring.statusDescription!) + 1
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
    }
  }

  const availableHiringMethods = {
    sendPolicyHolderDocument,
    checkExistClient,
    sendPolicyHolder,
    sendPayment,
    sendPets,
    sendCivilLiabilityInsured,
    checkExistClientCivilLiability
  }
  async function callToHiringMethod(
    methodName: keyof typeof availableHiringMethods,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    answers: PetsHiringAnswers
  ) {
    try {
      loadingStore.startLoading('common.loading.processing')

      if (methodName && methodName in availableHiringMethods) {
        await availableHiringMethods[methodName](answers)
      }
    } catch (error) {
      const err = error instanceof CustomError ? error.setDisplayType(errorDisplayType) : error
      errorManager.handleError(err)
      throw error
    }
  }
  function _setStepProcess(stepProcess: PetsInsuranceProcessStepEnum) {
    const currentStepCode = hiringAnswers.value.processStatus.stepCode ?? 0
    if (currentStepCode < stepProcess) {
      hiringAnswers.value.processStatus.stepCode = stepProcess
    }
  }
  async function sendPolicyHolderDocument(answers: PetsHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_DOCUMENT_PETS
    const quotationId = answers.quotationId ?? ''
    const holder = answers.policyHolder
    const request = await PetsMapper.SaveDocumentMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(PetsInsuranceProcessStepEnum.POLICY_HOLDER_SECOND)
    toastStore.successToast('health.saved-holder-data-and-document')
  }

  async function checkExistClient(answers: PetsHiringAnswers) {
    const policyHolder = answers.policyHolder
    const endpoint = ENDPOINTS.MDW_HEALTH_EXIST_CLIENT
    const request = PetsMapper.ExistClientMapper.toRequest(policyHolder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const thirdPartiesAnswered = data.hasDataPD && data.clientExists
    policyHolder.updateAttributes({ thirdPartiesAnswered })
    setGloballyAnswers({ policyHolder })
  }

  async function checkExistClientCivilLiability(answers: PetsHiringAnswers) {
    const civilLiabilityInsured = answers.civilLiabilityInsured
    const endpoint = ENDPOINTS.MDW_HEALTH_EXIST_CLIENT
    const request = PetsMapper.ExistClientMapper.toRequest(civilLiabilityInsured)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const thirdPartiesAnswered = data.hasDataPD && data.clientExists
    civilLiabilityInsured.updateAttributes({ thirdPartiesAnswered })
    setGloballyAnswers({ civilLiabilityInsured })
  }

  async function sendPolicyHolder(answers: PetsHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_HOLDER_PETS
    const quotationId = answers.quotationId ?? ''
    const holder = answers.policyHolder
    const request = PetsMapper.SavePolicyHolderMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(PetsInsuranceProcessStepEnum.PETS)

    toastStore.successToast('health.saved-holder-data')
  }

  async function sendPets(answers: PetsHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_INSURED_PETS
    const quotationId = answers.quotationId ?? ''

    const request = PetsCommonMapper.SavePetsMapper.toRequest(
      quotationId,
      answers.pets[answers.currentPetIndex]
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const needsCivilLiability = answers.needsCivilLiabilityStep()
    _setStepProcess(
      needsCivilLiability
        ? PetsInsuranceProcessStepEnum.CIVIL_LIABILITY
        : PetsInsuranceProcessStepEnum.PAYMENT
    )
    toastStore.successToast(
      t('pets.saved-insured-data', { riskNumber: answers.currentPetIndex + 1 })
    )
  }

  async function sendCivilLiabilityInsured(answers: PetsHiringAnswers) {
    const endpoint = ENDPOINTS.CIVIL_LIABILITY_RESPONSIBLE
    const quotationId = answers.quotationId ?? ''
    const request = PetsMapper.SaveCivilLiabilityInsuredMapper.toRequest(
      quotationId,
      answers.isLiabilityInsuredPolicyholder!,
      answers.civilLiabilityInsured
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)
    _setStepProcess(PetsInsuranceProcessStepEnum.PAYMENT)
    toastStore.successToast(t('pets.saved-rc-insured-data'))
  }

  async function sendPayment(answers: PetsHiringAnswers) {
    const quotationId = answers.quotationId ?? ''
    const bankDetails = answers.bankDetails

    const { error, data } = await commonStore.sendPayment(
      quotationId,
      bankDetails,
      ENDPOINTS.SAVE_PAYMENT_PETS
    )
    if (error) throw new ServiceError(data)

    const statusCode = data.codeStateEmission
    const { PENDING_EMISSION, PENDING, ISSUED } = InsuranceState
    if (ISSUED === statusCode) {
      const hash = btoa(quotationId)
      router.push({ name: 'pets-hiring-done', params: { hash } })
      const modalMessage = new Message({
        title: t('copy_hiring-status_emitted-modal_title'),
        label: t('copy_hiring-status_emitted-modal_label'),
        buttons: [
          new ModalButton({
            text: 'copy_hiring-status_pending-modal_button',
            navTo: 'exit'
          })
        ]
      })
      messageStore.successModal(modalMessage)
    } else if ([PENDING_EMISSION, PENDING].includes(statusCode)) {
      const modalMessage = new Message({
        title: t('copy_hiring-status_pending-modal_title'),
        label: t('copy_hiring-status_pending-modal_label'),
        extra: {
          hiringNumber: answers?.product?.quotationNumber,
          mailReminderAfterEmission: t('components.insurance-process.info2')
        },
        buttons: [
          new ModalButton({
            text: 'copy_hiring-status_pending-modal_button',
            navTo: 'exit'
          })
        ]
      })
      messageStore.pendingModal(modalMessage)
    }
    const hash = btoa(quotationId)
    router.push({ name: 'pets-hiring-done', params: { hash } })
    loadingStore.stopLoading()
  }

  function isProcessExpired(): boolean {
    const statusCode = hiringAnswers.value.processStatus.statusCode
    return statusCode === InsuranceProcessStatusEnum.EXPIRED
  }

  async function getBreed(petTypeCode: number): Promise<PetBreed[]> {
    try {
      const strPetTypeCode = String(petTypeCode)
      if (breeds.value[strPetTypeCode]) {
        if (breeds.value[strPetTypeCode].value?.length) {
          return breeds.value[strPetTypeCode].value!
        }
        if (breeds.value[strPetTypeCode]?.pendingRequest) {
          return await breeds.value[strPetTypeCode].pendingRequest!
        }
      } else {
        breeds.value[strPetTypeCode] = {}
      }
      loadingStore.startLoading('common.loading.processing')
      const endpoint = ENDPOINTS.GET_BREED

      const request = new GetPetBreedRequest({
        petTypeCode
      })

      breeds.value[strPetTypeCode].pendingRequest = MiddlewareService.get(
        endpoint + `?petTypeCode=${request.petTypeCode}`
      )
        .then((resp) => {
          if (!resp.error) {
            const newResp = { ...resp, data: new GetPetBreedResponse(resp.data).breeds }
            breeds.value[strPetTypeCode].value = newResp.data
            return newResp.data
          }
          throw new ServiceError(resp.data)
            .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
            .setTitle('copy_rater_error_retrieving_coverages')
        })
        .finally(() => (breeds.value[strPetTypeCode].pendingRequest = undefined))

      await breeds.value[strPetTypeCode].pendingRequest

      loadingStore.stopLoading()
      return breeds.value[strPetTypeCode].value!
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
      throw error
    }
  }

  async function getCoverages() {
    try {
      if (coverages.value.value?.coverageInfoData?.length) {
        return coverages.value.value
      }
      if (coverages.value?.pendingRequest) {
        return await coverages.value.pendingRequest!
      }
      loadingStore.startLoading('common.loading.processing')
      const endpoint = ENDPOINTS.GET_COVERAGES_COMPLETE

      const request = new RetrieveCoveragesCompleteRequest({
        businessLine: globalConfig.value.businessLineId
      })
      coverages.value.pendingRequest = MiddlewareService.post(endpoint, request)
        .then((resp) => {
          if (!resp.error) {
            const newResp = { ...resp, data: new RetrieveCoveragesCompleteResponse(resp.data) }
            coverages.value.value = newResp.data
            return newResp.data
          }
          throw new ServiceError(resp.data)
            .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
            .setTitle('copy_rater_error_retrieving_coverages')
        })
        .finally(() => (coverages.value.pendingRequest = undefined))
      await coverages.value.pendingRequest
      loadingStore.stopLoading()
      return coverages.value.value
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
      throw error
    }
  }

  const guaranteesItems = computed(
    () =>
      coverages.value?.value?.coverageInfoData
        ?.filter((cid) => cid.coverageInfoCapitals[0].sumInsured === '')
        ?.map((cid) => ({
          text: cid.description,
          coverageCode: cid.coverageCode,
          sumInsured: cid.coverageInfoCapitals[0].sumInsured
        }))
  )

  const additionalGuaranteesItems = computed(
    () =>
      coverages.value?.value?.coverageInfoData
        ?.filter((cid) => cid.coverageInfoCapitals[0].sumInsured !== '')
        ?.map((cid) => {
          let capital = cid.coverageInfoCapitals
            .map((cic) => priceFormat(cic.sumInsured, 0))
            .join(', ')
          capital = capital.replace(new RegExp(', (?!.*, )', 'i'), ' y ')
          return {
            text: cid.description,
            description: `${t('pets.capital')} ${capital}${CURRENCY}`,
            coverageCode: cid.coverageCode,
            sumInsured: cid.coverageInfoCapitals[0].sumInsured,
            range:
              cid.coverageInfoCapitals.length > 1
                ? cid.coverageInfoCapitals.map((cic) => Number(cic.sumInsured))
                : undefined
          }
        })
  )

  async function validateChip(chipNumber: number): Promise<ValidateChip | undefined> {
    try {
      const endpoint = `${ENDPOINTS.VALIDATE_CHIP}?chipNumber=${chipNumber}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) throw new ServiceError(data)

      return new ValidateChip(data)
    } catch (error) {
      errorManager.handleError(error)
      return undefined
    }
  }

  function getBranchName(): string {
    return 'pets'
  }

  function missingPetsNames(): boolean {
    return !!hiringAnswers.value.pets.find((pet) => !pet.petName)
  }

  return {
    stepsBeforeSaveAndExit,
    additionalGuaranteesItems,
    guaranteesItems,

    getBranchConfiguration,
    loadConfiguration,
    missingPetsNames,
    setGloballyAnswers,
    getHiringAnswers,

    getNewRaterPrice,
    hireProduct,

    isProcessExpired,
    fetchHiringFormAnswers,
    callToHiringMethod,
    getBreed,
    sendPets,
    sendCivilLiabilityInsured,
    getEmittedPolicyStatus,
    getCoverages,
    validateChip,
    getBranchName
  }
})
